import React from 'react';
import clsx from 'clsx';
import { signIn } from 'next-auth/client';
import { useRouter } from 'next/router';
import TagManager from 'react-gtm-module';

import { useRegisterMutation } from '@edx/graphql/generated';
import { useAppConfigStateContext } from '@edx/contexts/app-config';
import { useValidationErrors } from '@edx/hooks/use-validation-errors';
import { useTrackedCampaigns } from '@edx/hooks/use-track-campaigns';
import { Link } from '@edx/components/link';
import { Button } from '@edx/components/button';
import { TextInput } from '@edx/components/form';
import { IconCheck } from '@edx/components/icons';
import { useRevealPassword } from '@edx/hooks/use-reveal-password';
import { LoadingSpinner } from '@edx/components/loading-spinner';

type Props = {
  callbackUrl?: string;
};

export const RegisterForm: React.FC<Props> = ({ callbackUrl }) => {
  const { projectShortName } = useAppConfigStateContext();
  const router = useRouter();
  const campaign = useTrackedCampaigns(`${projectShortName}::campaign`);
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [emailConfirmation, setEmailConfirmation] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [terms, setTerms] = React.useState(false);
  const { showPassword, toggleRevealPassword, RevealPasswordIcon } = useRevealPassword();

  const [onRegister, { loading: isSubmitting, error }] = useRegisterMutation();
  const { errors: validationErrors } = useValidationErrors(error?.graphQLErrors);

  const handleInput = (setter: React.Dispatch<React.SetStateAction<string>>) => (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => setter(event.target.value);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (!isSubmitting) {
      const { data } = await onRegister({
        variables: {
          input: {
            firstName,
            lastName,
            email,
            email_confirmation: emailConfirmation,
            password,
            terms: terms ? 'yes' : 'no',
            campaign,
          },
        },
      });

      callbackUrl ||= router.query?.plan ? `/suscripcion?plan=${router.query.plan}` : '/suscripcion';

      TagManager.dataLayer({ dataLayer: { event: 'userCreated' } });

      return (
        data?.register &&
        signIn('credentials', {
          ...data?.register.tokens,
          email,
          firstName: data?.register.tokens?.user?.firstName,
          callbackUrl,
        })
      );
    }
  };

  const toggleTerms = (event: React.MouseEvent<HTMLLabelElement, MouseEvent>): void => {
    event.preventDefault();

    setTerms(!terms);
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center">
        <h3 className="font-bold text-lg">Datos personales</h3>

        <p className="text-gray-500 text-sm font-medium">
          ¿Tienes una cuenta?{' '}
          <Link href="/login" className="transition duration-150 text-gray-600 hover:text-gray-800">
            Identifícate
          </Link>
        </p>
      </div>

      <form className="mt-5 grid grid-cols-12 gap-4 text-gray-600" method="post" onSubmit={handleSubmit}>
        <TextInput
          variant="light"
          className="mt-1 font-medium"
          label="Nombre"
          labelClassName="col-span-12 md:col-span-4 flex flex-col"
          type="text"
          id="firstName"
          name="firstName"
          error={validationErrors.firstName}
          onChange={handleInput(setFirstName)}
          value={firstName}
          required
        />

        <TextInput
          variant="light"
          className="mt-1 font-medium"
          label="Apellidos"
          labelClassName="col-span-12 md:col-span-8 flex flex-col"
          type="text"
          id="lastName"
          name="lastName"
          error={validationErrors.lastName}
          onChange={handleInput(setLastName)}
          value={lastName}
          required
        />

        <TextInput
          variant="light"
          className="mt-1 font-medium"
          label="Correo electrónico"
          labelClassName="col-span-12 md:col-span-6 flex flex-col"
          type="email"
          id="email"
          name="email"
          error={validationErrors.email}
          onChange={handleInput(setEmail)}
          value={email}
          required
        />

        <TextInput
          variant="light"
          className="mt-1 font-medium"
          label="Confirma tu correo electrónico"
          labelClassName="col-span-12 md:col-span-6 flex flex-col"
          type="email"
          id="emailConfirmation"
          name="emailConfirmation"
          error={validationErrors.emailConfirmation}
          onChange={handleInput(setEmailConfirmation)}
          value={emailConfirmation}
          required
        />

        <div className="mt-1 col-span-12 md:col-span-6 flex flex-col">
          <TextInput
            variant="light"
            className="w-full font-medium"
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            error={validationErrors.password}
            onChange={handleInput(setPassword)}
            value={password}
            required
          >
            <RevealPasswordIcon
              className="absolute inset-y-0 my-auto right-3 h-6 w-5 cursor-pointer"
              onClick={toggleRevealPassword}
            />
          </TextInput>
        </div>

        <label
          htmlFor="terms"
          className="col-span-12 cursor-pointer duration-150 flex font-body hover:text-gray-800 items-start justify-start transition"
          onClick={toggleTerms}
        >
          <div
            className={clsx(
              'cursor-pointer border-2 rounded w-5 h-5 flex flex-shrink-0 justify-center items-center mr-2 overflow-auto',
              terms ? 'border-primary' : 'border-gray-500'
            )}
          >
            <input
              type="checkbox"
              name="terms"
              id="terms"
              className="cursor-pointer opacity-0 absolute"
              checked={terms}
              readOnly
            />
            {terms && <IconCheck className="fill-current w-3 h-3 text-primary-500 pointer-events-none" />}
          </div>
          <div className="flex flex-wrap text-sm">
            Acepto las&nbsp;
            <Link href="/aviso-legal" target="_blank" className="whitepace-no-wrap hover:underline">
              condiciones,&nbsp;
            </Link>
            la&nbsp;
            <Link href="/privacidad" target="_blank" className="whitepace-no-wrap hover:underline">
              política de datos&nbsp;
            </Link>
            y el&nbsp;
            <Link href="/cookies" target="_blank" className="whitepace-no-wrap hover:underline">
              uso de cookies
            </Link>
            .
          </div>

          {validationErrors.terms && <small>{validationErrors.terms}</small>}
        </label>

        <div className="mt-4 col-span-12">
          <Button type="submit" textVariant="medium" disabled={isSubmitting}>
            {isSubmitting && <LoadingSpinner className="text-white" />}
            Continuar
          </Button>
        </div>
      </form>
    </div>
  );
};
